import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Swiper from "swiper/bundle";
import "swiper/swiper-bundle.css";

// bskr/stack-slider
//
// Desktop (>=768px): per-card reveal-on-scroll with GSAP + ScrollTrigger.
// Mobile  (<768px) : Swiper slider with pagination dots, no arrows.
//
// The breakpoint swap goes through gsap.matchMedia(), which guarantees that
// ScrollTriggers created in the desktop branch get killed when the viewport
// shrinks below 768px, and that the Swiper instance gets destroyed (and the
// runtime `.swiper-slide` class removed) when it grows back above. No manual
// resize listener needed.

gsap.registerPlugin(ScrollTrigger);

const MOBILE_QUERY = "(max-width: 767.98px)";
const DESKTOP_QUERY = "(min-width: 768px)";

function init() {
    // Skip inside the Gutenberg editor — the live preview would animate cards
    // and instantiate Swiper on every block re-render, which is jarring while
    // editing. Same guard pattern as bskr/sticker.
    if (typeof window !== "undefined" && window.wp && window.wp.data) return;

    document.querySelectorAll(".stack-slider").forEach(setupBlock);
}

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
} else {
    init();
}

function setupBlock(root) {
    const track = root.querySelector(".stack-slider__track");
    if (!track) return;

    // ACF wraps InnerBlocks output in a `.acf-innerblocks-container` div on
    // the frontend. Unwrap it so the cards become direct children of
    // `.swiper-wrapper` (Swiper requires direct `.swiper-slide` children).
    // Same pattern as carousel.js.
    flattenAcfContainer(track);

    const cards = groupChildrenIntoCards(track);
    if (!cards.length) return;

    cards.forEach((card, i) => {
        card.classList.add("stack-slider__card-wrap");
        card.dataset.cardIndex = String(i);
    });

    const mm = gsap.matchMedia();
    let swiperInstance = null;

    mm.add(DESKTOP_QUERY, () => {
        const reduceMotion = window.matchMedia(
            "(prefers-reduced-motion: reduce)"
        ).matches;

        if (reduceMotion) {
            cards.forEach((card) => {
                gsap.set(card, { autoAlpha: 1, y: 0 });
            });
            return;
        }

        cards.forEach((card) => {
            gsap.fromTo(
                card,
                { autoAlpha: 0, y: 60 },
                {
                    autoAlpha: 1,
                    y: 0,
                    duration: 0.9,
                    ease: "expo.out",
                    scrollTrigger: {
                        trigger: card,
                        start: "top 85%",
                        once: true,
                    },
                }
            );
        });
    });

    mm.add(MOBILE_QUERY, () => {
        cards.forEach((card) => card.classList.add("swiper-slide"));

        const viewport = root.querySelector(".stack-slider__viewport");
        const pagination = root.querySelector(".stack-slider__pagination");
        const btnPrev = root.querySelector(".stack-slider__button-prev");
        const btnNext = root.querySelector(".stack-slider__button-next");

        swiperInstance = new Swiper(viewport, {
            slidesPerView: 1.05,
            spaceBetween: 15,
            loop: false,
            navigation: btnPrev && btnNext
                ? { prevEl: btnPrev, nextEl: btnNext }
                : false,
            // Reset any GSAP-applied transforms left over from the desktop
            // branch on the first card before Swiper takes over.
            on: {
                beforeInit: () => {
                    cards.forEach((card) => {
                        gsap.set(card, {
                            clearProps: "transform,opacity,visibility",
                        });
                    });
                },
            },
            pagination: pagination
                ? { el: pagination, clickable: true }
                : false,
        });

        return () => {
            if (swiperInstance) {
                swiperInstance.destroy(true, true);
                swiperInstance = null;
            }
            cards.forEach((card) => card.classList.remove("swiper-slide"));
        };
    });
}

// Pull every child out of the ACF inner-blocks wrapper so the cards land as
// direct children of the swiper wrapper.
function flattenAcfContainer(wrapper) {
    const container = wrapper.querySelector(".acf-innerblocks-container");
    if (!container) return;
    while (container.firstChild) {
        wrapper.appendChild(container.firstChild);
    }
    container.remove();
}

// Groups InnerBlocks children into "cards". A card starts at each block
// rendered as an image (`figure.wp-block-image`); any `bskr/sticker` siblings
// that follow are wrapped into the same card so the sticker pins to that
// card's wrapper. If there are no images, each child becomes its own card
// (fallback for content that doesn't follow the expected pattern).
function groupChildrenIntoCards(wrapper) {
    const children = Array.from(wrapper.children).filter(
        (el) => !el.classList.contains("swiper-pagination")
    );
    if (!children.length) return [];

    const hasImage = children.some((el) =>
        el.matches("figure.wp-block-image, .wp-block-image")
    );

    const cards = [];
    let current = null;

    children.forEach((el) => {
        const isImage = el.matches("figure.wp-block-image, .wp-block-image");

        if (isImage || !hasImage) {
            current = document.createElement("div");
            wrapper.insertBefore(current, el);
            current.appendChild(el);
            cards.push(current);
            return;
        }

        // Non-image (e.g. bskr/sticker) — attach to the most recent card.
        if (current) {
            current.appendChild(el);
        } else {
            // Sticker appears before any image: give it its own card so it
            // still renders somewhere meaningful.
            current = document.createElement("div");
            wrapper.insertBefore(current, el);
            current.appendChild(el);
            cards.push(current);
        }
    });

    return cards;
}
